import React, { useState, useEffect } from "react";
import logo from "../../images/EnvironmentalLabsLogoHorizontal.png";
import { UpdateUserPassword, LogoutUser } from "../../services/usersService";
import { NotificationManager } from "react-notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faLock, 
  faEye, 
  faEyeSlash,
  faCheck,
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import { GetLoggedInUser } from "../../services/usersService";

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loggedInUser, setLoggedInUser] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    noCommonPatterns: true,
    passwordsMatch: false,
    isDifferentFromCurrent: true
  });

  useEffect(() => {
    const checkLoggedInUser = async () => {
      try {
        const logInUser = await GetLoggedInUser();
        if (logInUser) {
          setLoggedInUser(logInUser);
        }
      } catch (error) {
        console.error("Error checking logged-in user:", error);
      }
    };

    checkLoggedInUser();
  }, []);

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password);
    
    // Check for common weak patterns
    const commonPatterns = [
      /123456/,
      /password/i,
      /qwerty/i,
      /abc123/i,
      /admin/i,
      /welcome/i,
      /letmein/i,
      /monkey/i,
      /dragon/i,
      /sunshine/i,
      /iloveyou/i,
      /princess/i,
      /rockyou/i,
      /12345678/,
      /football/i,
      /master/i
    ];
    
    const noCommonPatterns = !commonPatterns.some(pattern => pattern.test(password));
    
    return {
      minLength,
      hasUppercase,
      hasLowercase,
      hasNumber,
      hasSpecialChar,
      noCommonPatterns
    };
  };

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    
    const validation = validatePassword(password);
    const passwordsMatch = password === confirmPassword && password !== "";
    const isDifferentFromCurrent = password !== currentPassword && password !== "";
    
    setPasswordValidation({
      ...validation,
      passwordsMatch,
      isDifferentFromCurrent
    });
  };

  const handleConfirmPasswordChange = (e) => {
    const password = e.target.value;
    setConfirmPassword(password);
    
    const passwordsMatch = password === newPassword && password !== "";
    
    setPasswordValidation(prev => ({
      ...prev,
      passwordsMatch
    }));
  };

  // Add handler for current password to re-validate when current password changes
  const handleCurrentPasswordChange = (e) => {
    const password = e.target.value;
    setCurrentPassword(password);
    
    // Re-validate new password against current password
    if (newPassword) {
      const isDifferentFromCurrent = newPassword !== password && newPassword !== "";
      setPasswordValidation(prev => ({
        ...prev,
        isDifferentFromCurrent
      }));
    }
  };

  const isPasswordValid = () => {
    return Object.values(passwordValidation).every(Boolean) && newPassword !== "";
  };

  const handleChangePassword = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      NotificationManager.error("All fields are required");
      return;
    }

    if (!isPasswordValid()) {
      NotificationManager.error("Please ensure your password meets all requirements");
      return;
    }

    if (newPassword !== confirmPassword) {
      NotificationManager.error("New passwords do not match");
      return;
    }

    if (newPassword === currentPassword) {
      NotificationManager.error("New password must be different from current password");
      return;
    }

    try {
      ChangePasswordFromService();
    } catch (error) {
      NotificationManager.error("Failed to change password.");
    }
  };

  async function ChangePasswordFromService() {
    try {
      const passwordPayload = {
        operator: loggedInUser.operator,
        password: currentPassword,
        newPassword: newPassword,
      };

      let response = await UpdateUserPassword(passwordPayload);
      NotificationManager.success("Password changed successfully.");
      await LogoutUser();
      localStorage.removeItem("loggedInUser");
      localStorage.removeItem("lastUser");
      localStorage.removeItem("sessionExpiryWarningsShown");
      window.location.pathname = "/";
      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordValidation({
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false,
        noCommonPatterns: true,
        passwordsMatch: false,
        isDifferentFromCurrent: true
      });
    } catch (error) {
      NotificationManager.error("Failed to change password. Please check your current password.");
    }
  }

  const ValidationIcon = ({ isValid }) => (
    <FontAwesomeIcon 
      icon={isValid ? faCheck : faTimes} 
      style={{ 
        color: isValid ? '#48bb78' : '#f56565',
        marginRight: '8px',
        fontSize: '12px'
      }} 
    />
  );

  const togglePasswordVisibility = (field) => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  return (
    <div
      className="container"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: "20px"
      }}
    >
      <div className="box" style={{ width: "450px", maxWidth: "100%" }}>
        <div style={{ textAlign: "center" }}>
          <a>
            <img
              src={logo}
              alt="ELI Logo"
              style={{ maxHeight: "55px", marginTop: "10px" }}
            />
          </a>
        </div>
        <h1 className="title" style={{ textAlign: "center" }}>
          Change Password
        </h1>

        {/* Current Password */}
        <div className="field">
          <label className="label">Current Password</label>
          <p className="control has-icons-left has-icons-right">
            <input
              className="input"
              type={showCurrentPassword ? "text" : "password"}
              placeholder="Enter current password"
              value={currentPassword}
              onChange={handleCurrentPasswordChange}
            />
            <span className="icon is-small is-left">
              <FontAwesomeIcon icon={faLock} />
            </span>
            <span 
              className="icon is-small is-right" 
              style={{ cursor: 'pointer', pointerEvents: 'auto' }}
              onClick={() => togglePasswordVisibility('current')}
            >
              <FontAwesomeIcon icon={showCurrentPassword ? faEyeSlash : faEye} />
            </span>
          </p>
        </div>

        {/* New Password */}
        <div className="field">
          <label className="label">New Password</label>
          <p className="control has-icons-left has-icons-right">
            <input
              className="input"
              type={showNewPassword ? "text" : "password"}
              placeholder="Enter new password"
              value={newPassword}
              onChange={handleNewPasswordChange}
            />
            <span className="icon is-small is-left">
              <FontAwesomeIcon icon={faLock} />
            </span>
            <span 
              className="icon is-small is-right" 
              style={{ cursor: 'pointer', pointerEvents: 'auto' }}
              onClick={() => togglePasswordVisibility('new')}
            >
              <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
            </span>
          </p>
        </div>

        {/* Confirm Password */}
        <div className="field">
          <label className="label">Confirm New Password</label>
          <p className="control has-icons-left has-icons-right">
            <input
              className="input"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
            />
            <span className="icon is-small is-left">
              <FontAwesomeIcon icon={faLock} />
            </span>
            <span 
              className="icon is-small is-right" 
              style={{ cursor: 'pointer', pointerEvents: 'auto' }}
              onClick={() => togglePasswordVisibility('confirm')}
            >
              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
            </span>
          </p>
        </div>

        {/* Password Requirements */}
        {newPassword && (
          <div className="field">
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#f7fafc', 
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              fontSize: '13px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#2d3748' }}>
                Password Requirements:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div>
                  <ValidationIcon isValid={passwordValidation.minLength} />
                  At least 8 characters long
                </div>
                <div>
                  <ValidationIcon isValid={passwordValidation.hasUppercase} />
                  Contains uppercase letter (A-Z)
                </div>
                <div>
                  <ValidationIcon isValid={passwordValidation.hasLowercase} />
                  Contains lowercase letter (a-z)
                </div>
                <div>
                  <ValidationIcon isValid={passwordValidation.hasNumber} />
                  Contains number (0-9)
                </div>
                <div>
                  <ValidationIcon isValid={passwordValidation.hasSpecialChar} />
                  Contains special character (!@#$%^&*)
                </div>
                <div>
                  <ValidationIcon isValid={passwordValidation.noCommonPatterns} />
                  Not a common weak password
                </div>
                <div>
                  <ValidationIcon isValid={passwordValidation.isDifferentFromCurrent} />
                  Different from current password
                </div>
                <div>
                  <ValidationIcon isValid={passwordValidation.passwordsMatch} />
                  Passwords match
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="field">
          <p className="control">
            <button
              className="button is-primary is-fullwidth"
              onClick={handleChangePassword}
              disabled={!isPasswordValid() || !currentPassword}
            >
              <span>Change Password</span>
            </button>
          </p>
        </div>

        {/* Security Tips */}
        <div className="field">
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#fffaf0', 
            border: '1px solid #fed7aa',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#9a3412'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Security Tips:</div>
            <ul style={{ margin: 0, paddingLeft: '15px' }}>
              <li>Use a unique password not used elsewhere</li>
              <li>Consider using a passphrase with random words</li>
              <li>Avoid personal information (names, dates, etc.)</li>
              <li>Change passwords regularly</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;